import React, { useState } from 'react';
import CustomInput from 'components/CustomInput/CustomInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Search from '@material-ui/icons/Search';
import Done from '@material-ui/icons/Done';
import styles from 'assets/jss/material-dashboard-pro-react/views/contactUs/registeringComplaintStyle';
import { makeStyles } from '@material-ui/core/styles';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import SingleSuggestion from './subcomponents/SingleSuggestion';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import classNames from 'classnames';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ICustomDropdownWithSearchProps {
	label: string;
	value: string;
	setValue: React.Dispatch<React.SetStateAction<string>>;
	suggestions: types.troubleTicketing.ISingleSuggestion[];
	loading: boolean;
	minSearchTextLength: number;
	onClick: (suggestion: types.troubleTicketing.ISingleSuggestion) => void;
	clear: () => void;
	selected?: boolean;
	maxLength?: number;
	helperText?: string;
	error?: boolean;
	numbersQuantity?: number;
	maxNumberOfSuggestions?: number;
}

const CustomDropdownWithSearch = ({
	label,
	value,
	setValue,
	suggestions,
	loading,
	minSearchTextLength,
	onClick,
	clear,
	selected,
	maxLength,
	helperText,
	error,
	numbersQuantity,
	maxNumberOfSuggestions,
}: ICustomDropdownWithSearchProps) => {
	const classes = useStyles();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [activeSuggestion, setActiveSuggestion] = useState<number>(0);

	const selectSuggestion = () => {
		if (activeSuggestion >= 0) {
			const selectedSuggestion = suggestions[activeSuggestion];
			if (selectedSuggestion) {
				setValue(selectedSuggestion.name);
				onClick(selectedSuggestion);
				setIsOpen(false);
			}
		}
	};

	const handleOnKeyDown = (event: KeyboardEvent) => {
		switch (event.key) {
			case 'ArrowDown':
				arrowDown();
				break;
			case 'ArrowUp':
				arrowUp();
				break;
			case 'Enter':
				selectSuggestion();
				break;
			default:
				return;
		}
	};

	const onChange = (event: React.FormEvent<HTMLInputElement>) => {
		clear();
		const _value = (event.target as HTMLInputElement).value;
		if (!maxLength || (maxLength && _value.length <= maxLength)) {
			setValue(_value);
			if (_value.length >= minSearchTextLength) {
				setIsOpen(true);
			} else {
				setIsOpen(false);
			}
		}
	};

	const arrowDown = () => {
		if (activeSuggestion < suggestions.length - 1) {
			setActiveSuggestion((index) => index + 1);
		}
	};

	const arrowUp = () => {
		if (activeSuggestion > 0) {
			setActiveSuggestion((index) => index - 1);
		}
	};

	return (
		<div className={classes.dropdownWrapper}>
			<CustomInput
				labelText={label}
				id="number"
				inputProps={{
					required: true,
					value,
					type: 'number',
					onKeyDown: handleOnKeyDown,
					autoComplete: 'off',
					onChange: onChange,
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								aria-label="register complaint search icon"
								className={classNames(classes.inputIcon, { [classes.doneInputIcon]: selected })}
							>
								{selected ? <Done /> : <Search />}
							</IconButton>
						</InputAdornment>
					),
				}}
				hideArrows={true}
				formControlProps={{
					fullWidth: true,
				}}
				helperText={helperText}
				error={error}
			/>
			<ConditionalRender
				show={isOpen}
				onTrue={() => (
					<div className={classes.suggestionsWrapper}>
						<ConditionalRender
							show={loading}
							onTrue={() => <CustomCircularProgress text={translate('loading')} loading transparent />}
							onFalse={() => (
								<div>
									<ConditionalRender
										show={suggestions.length > 0}
										onTrue={() => (
											<>
												{suggestions.map((singleSuggestion, index) => (
													<SingleSuggestion
														key={index}
														name={singleSuggestion.name}
														onClick={selectSuggestion}
														active={index === activeSuggestion}
														onMouseOver={() => setActiveSuggestion(index)}
														onMouseOut={() => setActiveSuggestion(-1)}
													/>
												))}
												<>
													{!!numbersQuantity && !!maxNumberOfSuggestions && (
														<BodyS>
															{translate(
																'more',
																numbersQuantity - maxNumberOfSuggestions + 1
															)}
														</BodyS>
													)}
												</>
											</>
										)}
										onFalse={() => (
											<div className={classes.noResultsWrapper}>
												<BodyS disableMargin color="secondary-light">
													{translate('no.results')}
												</BodyS>
											</div>
										)}
									/>
								</div>
							)}
						/>
					</div>
				)}
			/>
		</div>
	);
};

export default CustomDropdownWithSearch;
